/**
 * Regulatory Demands — what the regulator can ask for, when, and what it
 * costs to be ready: readiness by framework, the ninety-day calendar of
 * obligations and certificates, the limits crossed and the rules that carry
 * them, the undertakings the airport has given, and the thresholds still
 * proposed rather than agreed.
 *
 * Pure. The page gathers, the desk decides.
 */
import type { Alert, AlertExplanation } from "./alerts";
import type { ComplianceReport, Obligation, Rule } from "./compliance";
import type { RateCard, SourceRef } from "./decision";
import { t, tn } from "../i18n";
import { money } from "./intelligence";
import type { Kpi } from "./kpis";
import type { DocumentRow } from "./library";
import { isCertificate } from "./certificates";
import type { TestingSummary } from "./testing";

export interface Undertaking {
  id: string;
  to: string;
  what: string;
  due: string;
  owner: string;
  status: "open" | "met" | "missed";
}

export interface RegulatoryEntered {
  inspectionAt?: string;
  inspector?: string;
  undertakings?: Undertaking[];
  enteredBy?: string;
  enteredAt?: string;
}

export interface CalendarItem {
  at: string;
  days: number;
  kind: "obligation" | "certificate" | "undertaking" | "inspection";
  title: string;
  framework?: string;
  state: "overdue" | "due" | "expiring" | "expired" | "open" | "missed" | "scheduled";
  sources: SourceRef[];
}

export interface Crossed {
  alert: Alert;
  rule?: Rule;
  reference?: string;
  costToClose: number;
  sources: SourceRef[];
}

export interface RegulatoryInputs {
  now: Date;
  siteCode: string;
  compliance: ComplianceReport | null;
  rules: Rule[];
  alerts: Alert[];
  explanations: Record<string, AlertExplanation | undefined>;
  documents: DocumentRow[];
  testing: TestingSummary | null;
  kpis: Kpi[];
  rateCard: RateCard;
  entered: RegulatoryEntered;
}

export interface RegulatoryDesk {
  generatedAt: string;
  currency: string;
  tiles: {
    key: string;
    label: string;
    value: number | null;
    unit: string;
    text: string;
    tone?: "good" | "bad" | "warn";
    assumed?: boolean;
    sources: SourceRef[];
  }[];
  frameworks: { framework: string; total: number; met: number; overdue: number; pct: number; pending: number }[];
  calendar: CalendarItem[];
  crossed: Crossed[];
  unverified: Rule[];
  proposed: Kpi[];
  undertakings: Undertaking[];
  costToClose: number;
  boardLine: string[];
  gaps: string[];
  figures: Set<string>;
}

const DAY = 86_400_000;

export function buildRegulatoryDesk(inp: RegulatoryInputs): RegulatoryDesk {
  const cur = inp.rateCard.currency;
  const today = inp.now.toISOString().slice(0, 10);
  const days = (d: string) => Math.ceil((new Date(d).getTime() - inp.now.getTime()) / DAY);
  const comp = inp.compliance;
  const obligations = comp?.obligations ?? [];
  const frameworks = (comp?.readiness ?? []).map((r) => ({
    ...r,
    pending: obligations.filter((o) => o.framework === r.framework && o.status === "pending").length,
  }));
  const oblRef = (o: Obligation): SourceRef => ({
    kind: "obligation",
    ref: o.periodKey ?? o.title,
    label: `${o.framework}: ${o.title}`,
    href: "/compliance",
  });

  /* the calendar: the next ninety days, and everything already overdue or expired */
  const calendar: CalendarItem[] = [];
  for (const o of obligations) {
    if (!o.dueAt) continue;
    const d = days(o.dueAt);
    if (o.status === "overdue")
      calendar.push({
        at: o.dueAt.slice(0, 10),
        days: d,
        kind: "obligation",
        title: o.title,
        framework: o.framework,
        state: "overdue",
        sources: [oblRef(o)],
      });
    else if (o.status === "pending" && d <= 90)
      calendar.push({
        at: o.dueAt.slice(0, 10),
        days: d,
        kind: "obligation",
        title: o.title,
        framework: o.framework,
        state: "due",
        sources: [oblRef(o)],
      });
  }
  for (const doc of inp.documents) {
    /* by the type's category: on the platform a certificate's type is the pack's key (aerodrome_certificate …) */
    if (!isCertificate(doc) || !doc.expiresOn) continue;
    const d = days(doc.expiresOn);
    if (doc.expiresOn < today)
      calendar.push({
        at: doc.expiresOn,
        days: d,
        kind: "certificate",
        title: doc.title,
        state: "expired",
        sources: [{ kind: "document", ref: doc.reference ?? doc.id, label: doc.title, href: `/library/${doc.id}` }],
      });
    else if (d <= 180)
      calendar.push({
        at: doc.expiresOn,
        days: d,
        kind: "certificate",
        title: doc.title,
        state: "expiring",
        sources: [{ kind: "document", ref: doc.reference ?? doc.id, label: doc.title, href: `/library/${doc.id}` }],
      });
  }
  for (const u of inp.entered.undertakings ?? []) {
    if (u.status === "met") continue;
    const d = days(u.due);
    calendar.push({
      at: u.due,
      days: d,
      kind: "undertaking",
      title: t("{what} — to {to}", { what: u.what, to: u.to }),
      state: u.status === "missed" || d < 0 ? "missed" : "open",
      sources: [
        { kind: "entered", ref: "undertaking", label: `${u.what} (${u.owner})`, enteredBy: inp.entered.enteredBy },
      ],
    });
  }
  if (inp.entered.inspectionAt)
    calendar.push({
      at: inp.entered.inspectionAt,
      days: days(inp.entered.inspectionAt),
      kind: "inspection",
      title: inp.entered.inspector
        ? t("Inspection by {inspector}", { inspector: inp.entered.inspector })
        : t("Inspection"),
      state: "scheduled",
      sources: [{ kind: "entered", ref: "inspection", label: t("inspection date"), enteredBy: inp.entered.enteredBy }],
    });
  calendar.sort((a, b) => a.days - b.days);

  /* limits crossed: severity-5 reactive alerts whose rule carries a reference */
  const crossed: Crossed[] = inp.alerts
    .filter((a) => a.status === "open" && a.kind === "reactive" && a.severity >= 5)
    .map((a) => {
      const rule = inp.rules.find((r) => r.key === a.ruleKey);
      const reference = inp.explanations[a.id]?.reference ?? rule?.reference;
      const category = /friction|pci|pavement|rubber|runway|taxiway/i.test(a.title)
        ? "pavement"
        : /agl|lamp|light/i.test(a.title)
          ? "agl"
          : /bridge|pbb/i.test(a.title)
            ? "pbb"
            : "other";
      return {
        alert: a,
        rule,
        reference,
        costToClose: inp.rateCard.categories[category].unitRepairNow,
        sources: [
          { kind: "alert", ref: a.id, label: a.title, href: `/alerts/${a.id}` },
          ...(rule ? [{ kind: "rule" as const, ref: rule.key, label: rule.name, href: "/compliance" }] : []),
        ],
      };
    });
  const unverified = inp.rules.filter((r) => r.enabled && r.verifyRequired && !r.verifiedAt);
  const proposed = inp.kpis.filter((k) => k.targetStatus === "proposed");
  const overdue = obligations.filter((o) => o.status === "overdue");
  const costToClose =
    crossed.reduce((t, c) => t + c.costToClose, 0) + overdue.length * inp.rateCard.categories.other.unitRepairNow;
  const inspection = inp.entered.inspectionAt ? days(inp.entered.inspectionAt) : null;
  const expired = calendar.filter((c) => c.state === "expired");
  const undertakings = inp.entered.undertakings ?? [];
  const open = undertakings.filter((u) => u.status !== "met");
  const tiles: RegulatoryDesk["tiles"] = [
    ...frameworks.map((f) => ({
      key: `fw:${f.framework}`,
      label: f.framework,
      value: f.pct,
      unit: "%",
      text: t("{met} of {total} met, {overdue} overdue, {pending} pending", {
        met: f.met,
        total: f.total,
        overdue: f.overdue,
        pending: f.pending,
      }),
      tone: (f.overdue ? "bad" : f.pct < 80 ? "warn" : "good") as "bad" | "warn" | "good",
      sources: [{ kind: "obligation" as const, ref: f.framework, label: t("readiness"), href: "/compliance" }],
    })),
    {
      key: "crossed",
      label: t("limits crossed"),
      value: crossed.length,
      unit: "",
      text: crossed.map((c) => `${c.alert.title}${c.reference ? ` (${c.reference})` : ""}`).join("; ") || t("none"),
      tone: crossed.length ? "bad" : "good",
      sources: crossed.flatMap((c) => c.sources),
    },
    {
      key: "expired",
      label: t("certificates expired"),
      value: expired.length,
      unit: "",
      text:
        expired.map((c) => c.title).join("; ") ||
        t("none; {n} expiring inside 180 days", { n: calendar.filter((c) => c.state === "expiring").length }),
      tone: expired.length ? "bad" : "good",
      sources: expired.flatMap((c) => c.sources),
    },
    {
      key: "cost",
      label: t("cost to close the findings"),
      value: costToClose,
      unit: cur,
      text: t("{crossed} and {overdue} on the rate card", {
        crossed: tn(crossed.length, "{n} crossed limit", "{n} crossed limits"),
        overdue: tn(overdue.length, "{n} overdue obligation", "{n} overdue obligations"),
      }),
      assumed: true,
      sources: [{ kind: "rate_card", ref: "rate card", label: t("repair and evidence costs by category") }],
    },
    {
      key: "unverified",
      label: t("rules not verified"),
      value: unverified.length,
      unit: "",
      text: unverified.map((r) => r.name).join("; ") || t("every rule that needs verification has it"),
      tone: unverified.length ? "warn" : "good",
      sources: unverified
        .slice(0, 5)
        .map((r) => ({ kind: "rule" as const, ref: r.key, label: r.name, href: "/compliance" })),
    },
    {
      key: "inspection",
      label: t("days to the inspection"),
      value: inspection,
      unit: "days",
      text:
        inspection != null
          ? `${inp.entered.inspectionAt}${inp.entered.inspector ? ` — ${inp.entered.inspector}` : ""}`
          : t("no inspection date entered"),
      assumed: true,
      tone: inspection != null && inspection < 30 && (crossed.length || overdue.length) ? "bad" : undefined,
      sources: inp.entered.inspectionAt
        ? [{ kind: "entered", ref: "inspection", label: t("inspection date"), enteredBy: inp.entered.enteredBy }]
        : [],
    },
    {
      key: "undertakings",
      label: t("undertakings open"),
      value: open.length,
      unit: "",
      text: open.map((u) => t("{what} by {due}", { what: u.what, due: u.due })).join("; ") || t("none recorded"),
      tone: open.some((u) => u.status === "missed" || days(u.due) < 0) ? "bad" : "good",
      sources: [],
    },
  ];
  const testing = inp.testing;
  const verification = unverified.length
    ? t(
        "{unverified} rules are unverified and {proposed} KPI targets proposed — a finding the inspector can make without leaving the desk.",
        { unverified: unverified.length, proposed: proposed.length },
      )
    : proposed.length
      ? t("{proposed} KPI targets are still proposed rather than agreed.", { proposed: proposed.length })
      : t("every rule is verified and every target agreed.");
  const boardLine = [
    t(
      "{site} meets {met} of {total} obligations across {frameworks} frameworks; {overdue} overdue, {limits} crossed, {certificates} expired.",
      {
        site: inp.siteCode,
        met: frameworks.reduce((n, f) => n + f.met, 0),
        total: frameworks.reduce((n, f) => n + f.total, 0),
        frameworks: frameworks.length,
        overdue: overdue.length,
        limits: tn(crossed.length, "{n} limit", "{n} limits"),
        certificates: tn(expired.length, "{n} certificate", "{n} certificates"),
      },
    ),
    calendar.length
      ? t("Next up: {items}.", {
          items: calendar
            .slice(0, 3)
            .map((c) =>
              t("{title} ({state}, {when})", {
                title: c.title,
                state: t(c.state),
                when: c.days < 0 ? t("{n} days ago", { n: -c.days }) : t("in {n} days", { n: c.days }),
              }),
            )
            .join("; "),
        })
      : t("Nothing falls due in the next ninety days."),
    inspection != null
      ? t("Closing every finding costs about {cost} on the rate card, with {days} days to the inspection; {rest}", {
          cost: money(costToClose, cur),
          days: inspection,
          rest: verification,
        })
      : t("Closing every finding costs about {cost} on the rate card; {rest}", {
          cost: money(costToClose, cur),
          rest: verification,
        }),
  ];
  const material = materialForRegulatory({
    tiles,
    frameworks,
    calendar,
    crossed,
    unverified,
    proposed,
    undertakings,
    costToClose,
    boardLine,
    currency: cur,
  } as RegulatoryDesk);
  const figures = new Set<string>();
  for (const n of material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
    figures.add(n);
    figures.add(n.replace(/,/g, ""));
  }
  for (let n = 0; n <= 12; n++) figures.add(String(n));
  return {
    generatedAt: inp.now.toISOString(),
    currency: cur,
    tiles,
    frameworks,
    calendar,
    crossed,
    unverified,
    proposed,
    undertakings,
    costToClose,
    boardLine,
    gaps: [
      ...(comp ? [] : [t("the compliance register is not on the platform")]),
      ...(testing
        ? testing.overdue
          ? [t("{n} tests are overdue", { n: testing.overdue })]
          : []
        : [t("no testing summary")]),
      ...(inp.entered.inspectionAt ? [] : [t("no inspection date entered")]),
    ],
    figures,
  };
}

export const REGULATORY_MODEL_PROMPT = [
  "You are advising an airport's chief executive on regulatory readiness from the material given and nothing else.",
  "You may explain, prioritise and argue; you must not state any number that is not in the material.",
  "Say when a figure rests on a rate card or an entered value rather than a platform record. Be brief and direct.",
].join(" ");

export function materialForRegulatory(
  d: Pick<
    RegulatoryDesk,
    | "tiles"
    | "frameworks"
    | "calendar"
    | "crossed"
    | "unverified"
    | "proposed"
    | "undertakings"
    | "costToClose"
    | "boardLine"
    | "currency"
  >,
): string {
  const L: string[] = [];
  for (const t of d.tiles)
    L.push(
      `Tile ${t.label}: ${t.value == null ? "n/a" : Math.round(t.value * 10) / 10} ${t.unit} — ${t.text}${t.assumed ? " [rate card/entered]" : ""}`,
    );
  for (const f of d.frameworks)
    L.push(
      `Framework ${f.framework}: ${f.met} of ${f.total} met (${f.pct}%), ${f.overdue} overdue, ${f.pending} pending`,
    );
  for (const c of d.calendar)
    L.push(
      `Calendar ${c.at} (${c.days} days): ${c.kind} ${c.title} — ${c.state}${c.framework ? ` [${c.framework}]` : ""}`,
    );
  for (const c of d.crossed)
    L.push(
      `Crossed: ${c.alert.title} — ${c.reference ?? "no reference"}; ${Math.round(c.costToClose)} ${d.currency} to close on the rate card`,
    );
  for (const r of d.unverified) L.push(`Unverified rule: ${r.key} ${r.name}${r.reference ? ` (${r.reference})` : ""}`);
  for (const k of d.proposed) L.push(`Proposed target: ${k.label} ${k.target} ${k.unit}`);
  for (const u of d.undertakings) L.push(`Undertaking to ${u.to}: ${u.what}, due ${u.due}, ${u.owner}, ${u.status}`);
  L.push(`Cost to close: ${Math.round(d.costToClose)} ${d.currency}. Board: ${d.boardLine.join(" ")}`);
  return L.join("\n");
}

export function answerRegulatory(q: string, d: RegulatoryDesk): string | null {
  const t = q.toLowerCase();
  if (/due|next|calendar|when|ninety|90/.test(t))
    return (
      d.calendar
        .slice(0, 8)
        .map((c) => `${c.at}: ${c.title} — ${c.state}${c.framework ? ` (${c.framework})` : ""}`)
        .join("\n") || "Nothing falls due in the next ninety days."
    );
  if (/cross|limit|finding/.test(t))
    return (
      d.crossed
        .map((c) => `${c.alert.title}: ${c.reference ?? "no reference"}; ${money(c.costToClose, d.currency)} to close`)
        .join("\n") || "No limit is crossed."
    );
  if (/cost|close/.test(t))
    return `${money(d.costToClose, d.currency)} on the rate card to close the crossed limits and evidence the overdue obligations.`;
  if (/verif|rule/.test(t))
    return (
      d.unverified.map((r) => `${r.name}${r.reference ? ` — ${r.reference}` : ""}`).join("\n") ||
      "Every rule that needs verification has it."
    );
  if (/undertak|promis/.test(t))
    return (
      d.undertakings.map((u) => `${u.what} to ${u.to}, due ${u.due}, ${u.owner}: ${u.status}`).join("\n") ||
      "No undertakings recorded."
    );
  if (/framework|ready|readiness/.test(t))
    return d.frameworks
      .map((f) => `${f.framework}: ${f.pct}% — ${f.met} of ${f.total} met, ${f.overdue} overdue`)
      .join("\n");
  if (/board|summar/.test(t)) return d.boardLine.join(" ");
  return null;
}
